/**
 * Portfolio Health Card Component
 *
 * Displays portfolio health with dual visualizations:
 * 1. Stacked horizontal bar showing value distribution by health status
 * 2. Unit chart (dot matrix) showing individual holdings colored by status
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn, formatINRCompact } from "@/lib/utils";

// ============================================================================
// Types
// ============================================================================

/**
 * Health status ratings for holdings
 */
export type HealthStatus = "in-form" | "on-track" | "off-track" | "out-of-form";

/**
 * Holding data for the health visualization
 */
export interface HealthHolding {
  id: string;
  name: string;
  status: HealthStatus;
  value: number;
}

/**
 * Aggregated health data by status
 */
export interface HealthBreakdown {
  status: HealthStatus;
  count: number;
  value: number;
  percentage: number;
}

/**
 * Props for the PortfolioHealthCard component
 */
export interface PortfolioHealthCardProps {
  /**
   * Individual holdings with their health status
   */
  holdings: HealthHolding[];
  /**
   * Total portfolio value (for calculating percentages)
   */
  totalValue?: number;
  /**
   * Optional CSS class name
   */
  className?: string;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Status configuration with colors and labels
 */
const STATUS_CONFIG: Record<
  HealthStatus,
  {
    label: string;
    bgColor: string;
    dotColor: string;
    textColor: string;
  }
> = {
  "in-form": {
    label: "In-Form",
    bgColor: "bg-emerald-500",
    dotColor: "bg-emerald-500",
    textColor: "text-emerald-700 dark:text-emerald-400",
  },
  "on-track": {
    label: "On-Track",
    bgColor: "bg-sky-500",
    dotColor: "bg-sky-500",
    textColor: "text-sky-700 dark:text-sky-400",
  },
  "off-track": {
    label: "Off-Track",
    bgColor: "bg-amber-500",
    dotColor: "bg-amber-500",
    textColor: "text-amber-700 dark:text-amber-400",
  },
  "out-of-form": {
    label: "Out-of-Form",
    bgColor: "bg-rose-500",
    dotColor: "bg-rose-500",
    textColor: "text-rose-700 dark:text-rose-400",
  },
};

/**
 * Order of statuses for consistent display
 */
const STATUS_ORDER: HealthStatus[] = [
  "in-form",
  "on-track",
  "off-track",
  "out-of-form",
];

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Calculate health breakdown from holdings
 */
function calculateHealthBreakdown(
  holdings: HealthHolding[],
  totalValue?: number
): HealthBreakdown[] {
  const total = totalValue ?? holdings.reduce((sum, h) => sum + h.value, 0);

  const grouped = holdings.reduce(
    (acc, holding) => {
      if (!acc[holding.status]) {
        acc[holding.status] = { count: 0, value: 0 };
      }
      acc[holding.status].count += 1;
      acc[holding.status].value += holding.value;
      return acc;
    },
    {} as Record<HealthStatus, { count: number; value: number }>
  );

  return STATUS_ORDER.map((status) => ({
    status,
    count: grouped[status]?.count ?? 0,
    value: grouped[status]?.value ?? 0,
    percentage: total > 0 ? ((grouped[status]?.value ?? 0) / total) * 100 : 0,
  })).filter((b) => b.count > 0);
}


// ============================================================================
// Subcomponents
// ============================================================================

/**
 * Stacked horizontal bar showing value distribution
 */
function StackedValueBar({
  breakdown,
}: {
  breakdown: HealthBreakdown[];
}) {
  if (breakdown.length === 0) {
    return (
      <div className="h-8 w-full rounded-lg bg-muted flex items-center justify-center">
        <span className="text-sm text-muted-foreground">No data</span>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex h-8 w-full overflow-hidden rounded-lg">
        {breakdown.map((segment) => (
          <div
            key={segment.status}
            className={cn(
              STATUS_CONFIG[segment.status].bgColor,
              "flex items-center justify-center transition-all duration-300",
              "first:rounded-l-lg last:rounded-r-lg"
            )}
            style={{ width: `${Math.max(segment.percentage, 2)}%` }}
            title={`${STATUS_CONFIG[segment.status].label}: ${formatINRCompact(segment.value)} (${segment.percentage.toFixed(1)}%)`}
          >
            {segment.percentage >= 10 && (
              <span className="text-xs font-medium text-white truncate px-1">
                {segment.percentage.toFixed(0)}%
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Unit chart (dot matrix) showing individual holdings
 */
function UnitChart({
  holdings,
}: {
  holdings: HealthHolding[];
}) {
  if (holdings.length === 0) {
    return (
      <div className="flex items-center justify-center py-4">
        <span className="text-sm text-muted-foreground">No holdings</span>
      </div>
    );
  }

  // Sort holdings by status order for consistent visual grouping
  const sortedHoldings = [...holdings].sort((a, b) => {
    return STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status);
  });

  return (
    <div
      className="flex flex-wrap gap-1.5"
      role="img"
      aria-label={`Unit chart showing ${holdings.length} holdings`}
    >
      {sortedHoldings.map((holding) => (
        <div
          key={holding.id}
          className={cn(
            STATUS_CONFIG[holding.status].dotColor,
            "h-3 w-3 rounded-full transition-transform hover:scale-125",
            "cursor-pointer"
          )}
          title={`${holding.name}: ${STATUS_CONFIG[holding.status].label}`}
          role="img"
          aria-label={`${holding.name}: ${STATUS_CONFIG[holding.status].label}`}
        />
      ))}
    </div>
  );
}

/**
 * Legend showing status colors and counts
 */
function HealthLegend({
  breakdown,
}: {
  breakdown: HealthBreakdown[];
}) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-2">
      {STATUS_ORDER.map((status) => {
        const data = breakdown.find((b) => b.status === status);
        const config = STATUS_CONFIG[status];

        return (
          <div key={status} className="flex items-center gap-1.5">
            <div className={cn(config.dotColor, "h-2.5 w-2.5 rounded-full")} />
            <span className={cn("text-sm font-medium", config.textColor)}>
              {config.label}
            </span>
            {data && data.count > 0 && (
              <span className="text-sm text-muted-foreground">
                ({data.count})
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Summary statistics row
 */
function HealthSummary({
  breakdown,
  totalHoldings,
}: {
  breakdown: HealthBreakdown[];
  totalHoldings: number;
}) {
  const healthyCount = breakdown
    .filter((b) => b.status === "in-form" || b.status === "on-track")
    .reduce((sum, b) => sum + b.count, 0);

  const healthyPercentage =
    totalHoldings > 0 ? (healthyCount / totalHoldings) * 100 : 0;

  return (
    <div className="flex items-baseline gap-2">
      <span className="text-2xl font-bold text-foreground">
        {healthyPercentage.toFixed(0)}%
      </span>
      <span className="text-sm text-muted-foreground">
        of your portfolio is performing well ({healthyCount}/{totalHoldings}{" "}
        holdings)
      </span>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * Portfolio Health Card
 *
 * Displays a dual-view visualization of portfolio health:
 * - Stacked bar chart showing value distribution by health status
 * - Unit chart (dot matrix) showing individual holdings
 *
 * @example
 * ```tsx
 * <PortfolioHealthCard
 *   holdings={[
 *     { id: "1", name: "HDFC Equity", status: "in-form", value: 150000 },
 *     { id: "2", name: "ICICI Bluechip", status: "on-track", value: 120000 },
 *     { id: "3", name: "SBI Smallcap", status: "off-track", value: 80000 },
 *   ]}
 * />
 * ```
 */
export function PortfolioHealthCard({
  holdings,
  totalValue,
  className,
}: PortfolioHealthCardProps) {
  const breakdown = calculateHealthBreakdown(holdings, totalValue);

  return (
    <Card className={cn(className)}>
      <CardHeader>
        <CardTitle className="text-lg font-semibold">Portfolio Health</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Summary */}
        <HealthSummary breakdown={breakdown} totalHoldings={holdings.length} />

        {/* Stacked Bar - Value View */}
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-muted-foreground">
            By Value
          </h4>
          <StackedValueBar breakdown={breakdown} />
        </div>

        {/* Unit Chart - Count View */}
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-muted-foreground">
            By Holdings ({holdings.length} total)
          </h4>
          <UnitChart holdings={holdings} />
        </div>

        {/* Legend */}
        <HealthLegend breakdown={breakdown} />
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Skeleton Loader
// ============================================================================

/**
 * Skeleton loader for PortfolioHealthCard
 */
export function PortfolioHealthCardSkeleton({
  className,
}: {
  className?: string;
}) {
  return (
    <Card className={cn(className)}>
      <CardHeader>
        <Skeleton className="h-6 w-36" />
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Summary Skeleton */}
        <div className="flex items-baseline gap-2">
          <Skeleton className="h-8 w-16" />
          <Skeleton className="h-4 w-48" />
        </div>

        {/* Stacked Bar Skeleton */}
        <div className="space-y-2">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-8 w-full rounded-lg" />
        </div>

        {/* Unit Chart Skeleton */}
        <div className="space-y-2">
          <Skeleton className="h-4 w-32" />
          <div className="flex flex-wrap gap-1.5">
            {[...Array(12)].map((_, i) => (
              <Skeleton key={i} className="h-3 w-3 rounded-full" />
            ))}
          </div>
        </div>

        {/* Legend Skeleton */}
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <Skeleton className="h-2.5 w-2.5 rounded-full" />
              <Skeleton className="h-4 w-16" />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default PortfolioHealthCard;
